// Sprint-006 D5 (F-D4-2) — ux-review-sprint-006.md: `HandOutWorkFlow` held
// every field (job title, pieces, per-piece assignments, current step) in
// transient `useState`, so a backgrounded phone whose tab got silently
// discarded/reloaded by the OS (a documented iOS Safari/Android Chrome
// behavior, not hypothetical) would lose several minutes of real work with
// zero warning — the one multi-step surface in the app that didn't share
// the rest of the product's excellent offline/interruption durability
// story (`offline/queue.ts`/`offline/db.ts`).
//
// Minimum viable fix per the review: persist the in-progress draft to
// `sessionStorage` (not the offline-queue/IndexedDB layer — this state is
// never sent to the server until "Send it," so there is no idempotency-key/
// replay risk to manage, and `sessionStorage` alone already survives a
// backgrounded-tab reload within the same browser tab/session, which is
// exactly the failure mode described). Restore on mount, clear on
// successful send or explicit cancel.

export type HandOutStep = 1 | 2 | 3 | 4;

export interface HandOutPieceDraft {
  localId: string;
  title: string;
  assigneeId: string | null;
}

export interface HandOutDraft {
  step: HandOutStep;
  selectedExistingId: string | null;
  pickingNew: boolean;
  customTitle: string;
  pieces: HandOutPieceDraft[];
}

const STORAGE_KEY = "hayloft:handout-draft:v1";

const VALID_STEPS: HandOutStep[] = [1, 2, 3, 4];

function isValidDraft(value: unknown): value is HandOutDraft {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.step !== "number" || !VALID_STEPS.includes(v.step as HandOutStep)) return false;
  if (v.selectedExistingId !== null && typeof v.selectedExistingId !== "string") return false;
  if (typeof v.pickingNew !== "boolean") return false;
  if (typeof v.customTitle !== "string") return false;
  if (!Array.isArray(v.pieces)) return false;
  return v.pieces.every(
    (p) =>
      p &&
      typeof p === "object" &&
      typeof (p as Record<string, unknown>).localId === "string" &&
      typeof (p as Record<string, unknown>).title === "string" &&
      ((p as Record<string, unknown>).assigneeId === null ||
        typeof (p as Record<string, unknown>).assigneeId === "string"),
  );
}

/**
 * Best-effort save — `sessionStorage` can throw (private-mode/quota edge
 * cases); losing draft durability there is an acceptable degradation, but
 * it must never surface as a crash or block the flow (same "never trap the
 * user" principle the rest of the app follows).
 */
export function saveHandOutDraft(draft: HandOutDraft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable — nothing to do; the flow still works in-memory.
  }
}

export function loadHandOutDraft(): HandOutDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearHandOutDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
